<script setup lang="ts">
import { FunnelX, Search } from 'lucide-vue-next';
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';
import { toast } from 'vue3-toastify';
import { useRoute, useRouter } from 'vue-router';

import Pagination from '~/components/Pagination/Pagination.vue';
import ConfirmModal from '~/components/shared/ConfirmModal.vue';
import ExpandableCard from '~/components/shared/ExpandableCard.vue';
// eslint-disable-next-line perfectionist/sort-imports
import DashboardLayout from '~/layouts/DashboardLayout.vue';
// eslint-disable-next-line perfectionist/sort-imports
import useSearchParamsState from '~/hooks/useSearchParamsState';
// eslint-disable-next-line perfectionist/sort-imports
import { getCessionStatusColorClass } from '~/constants/capitalMarkets';
import { formatCurrencyForDisplay } from '~/utils/formatters';

import type {
  CessionFinancingStatusEnum,
  FinancingItem,
  FinancingOperationResult,
} from '../services/capitalMarketsService';

import AddFinancingsModal from '../components/AddFinancingsModal.vue';
import FinancingOperationResultModal from '../components/FinancingOperationResultModal.vue';
import useAddFinancingsToCession from '../queries/useAddFinancingsToCession';
import useCessionDetail from '../queries/useCessionDetail';
import useDownloadCessionCsv from '../queries/useDownloadCessionCsv';
import useFinalizeCession from '../queries/useFinalizeCession';
import useProcessCession from '../queries/useProcessCession';
import useRemoveFinancingsFromCession from '../queries/useRemoveFinancingsFromCession';
import useSyncInstallments from '../queries/useSyncInstallments';
import {
  FINANCING_STATUS_LABELS,
  getCessionStatusLabel,
  getFinancingStatusLabel,
  getPaymentStatusLabel,
} from '../utils/statusMapper';

const route = useRoute('/cessions.[id]');
const router = useRouter();

const id = computed(() => {
  const paramId = route.params.id;
  return typeof paramId === 'string' ? Number.parseInt(paramId, 10) : Number(paramId);
});

const paginationParams = useSearchParamsState({
  integration_status: undefined,
  page: 1,
  page_size: 20,
  search_query: undefined,
  search_type: undefined,
});

const paginationParamsRef = computed(() => {
  const params: {
    integration_status?: string;
    page: number;
    page_size: number;
    search_query?: string;
    search_type?: string;
  } = {
    page: Number(paginationParams.value.page) || 1,
    page_size: Number(paginationParams.value.page_size) || 20,
  };

  if (paginationParams.value.integration_status) {
    params.integration_status = paginationParams.value.integration_status;
  }
  if (paginationParams.value.search_query) {
    params.search_query = paginationParams.value.search_query;
  }
  if (paginationParams.value.search_type) {
    params.search_type = paginationParams.value.search_type;
  }

  return params;
});

const {
  data: cessionDetail,
  error,
  isError,
  isLoading,
} = useCessionDetail(id, paginationParamsRef);

const finalizeCessionMutation = useFinalizeCession();
const processCessionMutation = useProcessCession();
const syncInstallmentsMutation = useSyncInstallments();
const addFinancingsMutation = useAddFinancingsToCession();
const removeFinancingsMutation = useRemoveFinancingsFromCession();

const downloadCsvMutation = useDownloadCessionCsv();
const showFinalizeModal = ref(false);
const showRemoveModal = ref(false);
const showAddFinancingsModal = ref(false);
const showResultModal = ref(false);
const operationResults = ref<FinancingOperationResult[]>([]);
const operationType = ref<'add' | 'remove'>('add');
const showProcessModal = ref(false);
const actionsDropdownRef = ref<HTMLElement | null>(null);

const isBankingProvidersExpanded = ref(true);
const isFinancingsExpanded = ref(true);
const isLogsExpanded = ref(false);

const FINANCING_SEARCH_TYPE_OPTIONS = [
  { name: 'CCB', value: 'ccb' },
  { name: 'Financiamento ID', value: 'solfacil_financing_id' },
] as const;

const localSearchType = ref<'ccb' | 'solfacil_financing_id' | undefined>(undefined);
const localSearchQuery = ref('');
const selectedFinancingIds = ref<Set<number>>(new Set());

function syncLocalSearchFromParams(): void {
  if (paginationParams.value.search_type && paginationParams.value.search_query) {
    localSearchType.value = paginationParams.value.search_type as 'ccb' | 'solfacil_financing_id';
    localSearchQuery.value = paginationParams.value.search_query;
  } else {
    localSearchType.value = undefined;
    localSearchQuery.value = '';
  }
}

onMounted(() => {
  syncLocalSearchFromParams();
});

watch(
  () => [paginationParams.value.search_type, paginationParams.value.search_query],
  () => {
    syncLocalSearchFromParams();
  },
);

function closeDropdown(): void {
  if (actionsDropdownRef.value) {
    const dropdownElement = actionsDropdownRef.value.querySelector('[data-bs-toggle="dropdown"]');
    if (dropdownElement) {
      const dropdownMenu = actionsDropdownRef.value.querySelector('.dropdown-menu');
      if (dropdownMenu) {
        dropdownMenu.classList.remove('show');
      }
      dropdownElement.classList.remove('show');
      dropdownElement.setAttribute('aria-expanded', 'false');
    }
  }
}

watch(
  () => downloadCsvMutation.isPending.value,
  (isPending, wasPending) => {
    if (wasPending && !isPending) {
      closeDropdown();
    }
  },
);

const financings = computed(() => cessionDetail.value?.financings?.items || []);

const allSolfacilFinancingIds = computed(() => financings.value.map(
  (financing: FinancingItem) => financing.solfacil_financing_id,
));

const selectedSolfacilFinancingIds = computed(() => financings.value
  .filter((financing: FinancingItem) => selectedFinancingIds.value.has(financing.solfacil_financing_id))
  .map((financing: FinancingItem) => financing.solfacil_financing_id));

const canModifyFinancings = computed(() => {
  if (!cessionDetail.value) return false;
  const { status } = cessionDetail.value;
  const modifiableStatuses = [
    'in_creation',
    'in_progress',
    'failed_import_financing',
    'failed_import_installments',
    'pending_send_intermediator',
    'sync_installments',
    'synced_installments',
  ];
  return modifiableStatuses.includes(status);
});

function isFinancingDisabled(financing: FinancingItem): boolean {
  if (canModifyFinancings.value) {
    return false;
  }

  const enabledStatuses = [
    'awaiting_return_cession_intermediator',
    'sending_cession_intermediator',
    'pending_send_intermediator',
    'failure_send_cession_intermediator',
    'failed_backing_not_found',
  ];
  return !enabledStatuses.includes(financing.integration_status);
}

const allFinancingsSelected = computed(() => {
  if (financings.value.length === 0) return false;
  const enabledFinancings = financings.value.filter((financing: FinancingItem) => !isFinancingDisabled(financing));
  if (enabledFinancings.length === 0) return false;
  return enabledFinancings.every(
    (financing: FinancingItem) => selectedFinancingIds.value.has(financing.solfacil_financing_id),
  );
});

const someFinancingsSelected = computed(() => selectedFinancingIds.value.size > 0 && !allFinancingsSelected.value);

function confirmFinalizeCession(): void {
  const idsToSend = selectedSolfacilFinancingIds.value.length > 0
    ? selectedSolfacilFinancingIds.value
    : allSolfacilFinancingIds.value;

  finalizeCessionMutation.mutate({
    cessionId: id.value,
    solfacilFinancingIds: idsToSend,
  });
  showFinalizeModal.value = false;
  selectedFinancingIds.value.clear();
}

function confirmProcessCession(): void {
  if (!cessionDetail.value) return;

  const today = new Date().toISOString().split('T')[0];
  const agio = cessionDetail.value.premium || 0;

  processCessionMutation.mutate({
    agio,
    cededAt: today,
    cessionId: id.value,
  });

  showProcessModal.value = false;
}

function handleDownloadReport(): void {
  downloadCsvMutation.mutate(id.value);
}

function handleFinalizeCession(): void {
  showFinalizeModal.value = true;
}

function handleProcessCession(): void {
  showProcessModal.value = true;
}

function handleSyncInstallments(): void {
  syncInstallmentsMutation.mutate(id.value);
}

function toggleAllFinancingsSelection(): void {
  const newSet = new Set(selectedFinancingIds.value);

  if (allFinancingsSelected.value) {
    financings.value.forEach((financing: FinancingItem) => {
      newSet.delete(financing.solfacil_financing_id);
    });
  } else {
    financings.value.forEach((financing: FinancingItem) => {
      if (!isFinancingDisabled(financing)) {
        newSet.add(financing.solfacil_financing_id);
      }
    });
  }

  selectedFinancingIds.value = newSet;
}

function toggleFinancingSelection(financingId: number): void {
  const newSet = new Set(selectedFinancingIds.value);

  if (newSet.has(financingId)) {
    newSet.delete(financingId);
  } else {
    newSet.add(financingId);
  }

  selectedFinancingIds.value = newSet;
}

const canFinalize = computed(() => cessionDetail.value?.actions?.finalize ?? false);

const canSyncInstallments = computed(() => cessionDetail.value?.actions?.sync_installments ?? false);

const canProcessCession = computed(() => cessionDetail.value?.actions?.forward ?? false);

const isOverturning = computed(() => cessionDetail.value?.category === 'overturning');

const entityLabel = computed(() => (isOverturning.value ? 'Tombamento' : 'Cessão'));

const entityLabelLower = computed(() => (isOverturning.value ? 'tombamento' : 'cessão'));

const pageTitle = computed(() => `Detalhes do ${entityLabel.value}`);

const pageDescription = computed(() => `Detalhes do ${entityLabel.value} #${id.value}`);

const finalizeButtonLabel = computed(() => `Finalizar ${entityLabel.value}`);

const finalizeModalTitle = computed(() => `Finalizar ${entityLabel.value}`);

const finalizeModalMessage = computed(() => `Tem certeza que deseja finalizar este ${entityLabelLower.value}?`);

const processButtonLabel = computed(() => `Processar ${entityLabel.value}`);

const processModalTitle = computed(() => `Processar ${entityLabel.value}`);

const processModalMessage = computed(() => `Tem certeza que deseja processar este ${entityLabelLower.value}?`);

const logsTitle = computed(() => `Logs do ${entityLabel.value}`);

const breadcrumbItems = computed(() => [
  { label: 'Mercado de Capitais', to: '/cessions' },
  { label: `Detalhes do ${entityLabel.value}` },
]);

const bankingProviders = computed(() => {
  if (!cessionDetail.value?.aggregated_financing_values) {
    return [];
  }

  return Object.entries(cessionDetail.value.aggregated_financing_values).map(([key, values]) => ({
    name: key.toUpperCase(),
    ...values,
  }));
});

const financingsMetadata = computed(() => cessionDetail.value?.financings?.metadata);

const logs = computed(() => {
  if (!cessionDetail.value?.logs) {
    return [];
  }
  return [...cessionDetail.value.logs].reverse();
});

function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

function getLogTypeBadgeClass(type: string): string {
  const typeMap: Record<string, string> = {
    error: 'bg-danger-lt',
    info: 'bg-info-lt',
    warning: 'bg-warning-lt',
  };

  return typeMap[type] || 'bg-secondary-lt';
}

function getLogTypeLabel(type: string): string {
  const labelMap: Record<string, string> = {
    error: 'Erro',
    info: 'Informação',
    warning: 'Aviso',
  };

  return labelMap[type] || type;
}

const currentPage = computed(() => financingsMetadata.value?.current_page || 1);

const pageSize = computed(() => financingsMetadata.value?.page_size || 20);

const hasNext = computed(() => {
  if (!financingsMetadata.value) return false;
  return financingsMetadata.value.current_page < financingsMetadata.value.total_pages;
});

function formatDate(dateString?: null | string): string {
  if (!dateString) return '-';
  try {
    const date = new Date(`${dateString}T00:00:00`);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return dateString;
  }
}

const percentageFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 4,
  minimumFractionDigits: 0,
});

function formatPercentage(value: null | number | undefined, shouldMultiply = true): string {
  if (value === null || value === undefined) return '-';
  const percentage = shouldMultiply ? value * 100 : value;

  return `${percentageFormatter.format(percentage)}%`;
}

function getCessionStatusBadgeClass(status: string): string {
  const colorClass = getCessionStatusColorClass(status as any);
  const colorMap: Record<string, string> = {
    blue: 'bg-info-lt',
    gray: 'bg-secondary-lt',
    green: 'bg-success-lt',
    red: 'bg-danger-lt',
    yellow: 'bg-warning-lt',
  };

  return colorMap[colorClass] || 'bg-secondary-lt';
}

function getPaymentStatusBadgeClass(status: string): string {
  const statusMap: Record<string, string> = {
    completed: 'bg-success-lt',
    failed: 'bg-danger-lt',
    not_applicable: 'bg-secondary-lt',
    pending: 'bg-warning-lt',
  };

  return statusMap[status] || 'bg-secondary-lt';
}

function getStatusBadgeClass(status: string): string {
  const statusMap: Record<string, string> = {
    cancelled_by_user: 'bg-secondary-lt',
    cession_cancelled_by_intermediator: 'bg-warning-lt',
    done: 'bg-success-lt',
    failure_import_installments: 'bg-danger-lt',
    in_progress: 'bg-info-lt',
  };

  return statusMap[status] || 'bg-secondary-lt';
}

function handlePageChange(page: number): void {
  paginationParams.value.page = page;
}

function handlePageSizeChange(newPageSize: number): void {
  paginationParams.value.page = 1;
  paginationParams.value.page_size = newPageSize;
}

const statusOptions = computed(() => Object.entries(FINANCING_STATUS_LABELS).map(([value, label]) => ({
  label,
  value: value as CessionFinancingStatusEnum,
})));

async function confirmRemoveFinancings(): Promise<void> {
  try {
    const idsToRemove = Array.from(selectedFinancingIds.value);
    const response = await removeFinancingsMutation.mutateAsync({
      cessionId: id.value,
      solfacilFinancingIds: idsToRemove,
    });

    showRemoveModal.value = false;
    operationResults.value = response.results;
    operationType.value = 'remove';

    const successCount = response.results.filter((r) => r.success).length;
    const failureCount = response.results.filter((r) => !r.success).length;

    if (successCount > 0) {
      toast.success(`${successCount} financiamento(s) removido(s) com sucesso`);
      selectedFinancingIds.value.clear();
    }
    if (failureCount > 0) {
      toast.warning(`${failureCount} financiamento(s) não puderam ser removidos`);
    }

    showResultModal.value = true;
  } catch (_error) {
    showRemoveModal.value = false;
  }
}

async function handleAddFinancings(ids: number[]): Promise<void> {
  try {
    const response = await addFinancingsMutation.mutateAsync({
      cessionId: id.value,
      solfacilFinancingIds: ids,
    });

    showAddFinancingsModal.value = false;

    operationResults.value = response.results;
    operationType.value = 'add';

    const successCount = response.results.filter((r) => r.success).length;
    const failureCount = response.results.filter((r) => !r.success).length;

    if (successCount > 0) {
      toast.success(`${successCount} financiamento(s) adicionado(s) com sucesso`);
    }
    if (failureCount > 0) {
      toast.warning(`${failureCount} financiamento(s) não puderam ser adicionados`);
    }

    showResultModal.value = true;
  } catch (_error) {
    showAddFinancingsModal.value = false;
  }
}

function handleClearFilters(): void {
  (paginationParams.value as any).integration_status = undefined;
  (paginationParams.value as any).search_query = undefined;
  (paginationParams.value as any).search_type = undefined;
  localSearchType.value = undefined;
  localSearchQuery.value = '';
  paginationParams.value.page = 1;
}

function handleIntegrationStatusChange(event: Event): void {
  const target = event.target as HTMLSelectElement;
  (paginationParams.value as any).integration_status = target.value || undefined;
  paginationParams.value.page = 1;
}

function handleOpenAddFinancingsModal(): void {
  showAddFinancingsModal.value = true;
}

function handleRemoveFinancings(): void {
  if (selectedFinancingIds.value.size === 0) {
    toast.warning('Selecione ao menos um financiamento para remover');
    return;
  }
  showRemoveModal.value = true;
}

function handleSearch(): void {
  const searchValue = localSearchQuery.value.trim();

  if (searchValue && localSearchType.value) {
    (paginationParams.value as any).search_query = searchValue;
    (paginationParams.value as any).search_type = localSearchType.value;
  } else {
    (paginationParams.value as any).search_query = undefined;
    (paginationParams.value as any).search_type = undefined;
  }

  paginationParams.value.page = 1;
}

function navigateToFinancingDetail(financing: FinancingItem): void {
  router.push({
    path: `/cessions/${id.value}/financings/${financing.solfacil_financing_id}`,
    query: { ccb: financing.ccb },
  });
}
</script>

<template>
  <DashboardLayout :title="pageTitle" :description="pageDescription">
    <ol class="breadcrumb breadcrumb-arrows mb-2" aria-label="breadcrumbs">
      <li
        v-for="(item, index) in breadcrumbItems"
        :key="index"
        class="breadcrumb-item"
        :class="{ active: index === breadcrumbItems.length - 1 }"
        :aria-current="index === breadcrumbItems.length - 1 ? 'page' : undefined"
      >
        <router-link v-if="item.to" :to="item.to">{{ item.label }}</router-link>
        <span v-else>{{ item.label }}</span>
      </li>
    </ol>
    <template #actions>
      <div ref="actionsDropdownRef" class="dropdown">
        <button
          class="btn btn-primary dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          :disabled="isLoading || downloadCsvMutation.isPending.value"
        >
          <span v-if="downloadCsvMutation.isPending.value" class="spinner-border spinner-border-sm me-2" />
          Ações
        </button>
        <ul class="dropdown-menu dropdown-menu-end">
          <li>
            <button
              class="dropdown-item"
              type="button"
              :disabled="downloadCsvMutation.isPending.value"
              @click="handleDownloadReport"
            >
              <span v-if="downloadCsvMutation.isPending.value" class="spinner-border spinner-border-sm me-2" />
              Download Relatório
            </button>
          </li>
          <li>
            <button
              class="dropdown-item"
              type="button"
              :disabled="!canSyncInstallments || syncInstallmentsMutation.isPending.value"
              @click="handleSyncInstallments"
            >
              <span v-if="syncInstallmentsMutation.isPending.value" class="spinner-border spinner-border-sm me-2" />
              Baixar parcelas
            </button>
          </li>
          <li>
            <button
              class="dropdown-item"
              type="button"
              :disabled="!canProcessCession || processCessionMutation.isPending.value"
              @click="handleProcessCession"
            >
              <span v-if="processCessionMutation.isPending.value" class="spinner-border spinner-border-sm me-2" />
              {{ processButtonLabel }}
            </button>
          </li>
          <li><hr class="dropdown-divider" /></li>
          <li>
            <button
              class="dropdown-item"
              type="button"
              :disabled="!canFinalize || finalizeCessionMutation.isPending.value"
              @click="handleFinalizeCession"
            >
              <span v-if="finalizeCessionMutation.isPending.value" class="spinner-border spinner-border-sm me-2" />
              {{ finalizeButtonLabel }}
            </button>
          </li>
        </ul>
      </div>
    </template>
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <div v-else-if="isError" class="alert alert-danger" role="alert">
      <strong>Erro ao carregar dados:</strong> {{ error?.message || 'Erro desconhecido' }}
    </div>

    <div v-else-if="cessionDetail" class="row g-3">
      <div v-if="cessionDetail.status" class="col-12 mb-2">
        <span
          class="badge"
          :class="getCessionStatusBadgeClass(cessionDetail.status)"
        >
          {{ getCessionStatusLabel(cessionDetail.status) }}
        </span>
      </div>
      <div class="col-12">
        <ExpandableCard
          :key="`banking-${id}`"
          title="Valores por Provedor Bancário"
          :default-expanded="isBankingProvidersExpanded"
          @update:expanded="isBankingProvidersExpanded = $event"
        >
          <div v-if="bankingProviders.length === 0" class="text-muted text-center py-4">
            Nenhum provedor bancário encontrado
          </div>

          <div v-else>
            <div class="row mb-0 mb-md-3">
              <div class="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-column align-items-center text-center">
                      <span class="text-muted mb-2">Valor Bruto Total</span>
                      <span class="fw-bold text-primary">
                        {{ formatCurrencyForDisplay(cessionDetail.total_gross_amount) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-column align-items-center text-center">
                      <span class="text-muted mb-2">Valor Financiado Total</span>
                      <span class="fw-bold text-success">
                        {{ formatCurrencyForDisplay(cessionDetail.total_financed_amount) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-column align-items-center text-center">
                      <span class="text-muted mb-2">Valor de Aquisição Total</span>
                      <span class="fw-bold text-info">
                        {{ formatCurrencyForDisplay(cessionDetail.total_acquisition_amount) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-column align-items-center text-center">
                      <span class="text-muted mb-2">Ágio</span>
                      <span class="fw-bold text-warning">
                        {{ cessionDetail.premium !== null ? formatPercentage(cessionDetail.premium, false) : '-' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-12 col-sm-6 col-md-3">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-column align-items-center text-center">
                      <span class="text-muted mb-2">TIR</span>
                      <span class="fw-bold text-secondary">
                        {{ formatPercentage(cessionDetail.irr) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th scope="col">Provedor Bancário</th>
                    <th scope="col">Valor Bruto</th>
                    <th scope="col">Valor Financiado</th>
                    <th scope="col">Valor de Aquisição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="provider in bankingProviders" :key="provider.name">
                    <td>
                      <strong>{{ provider.name }}</strong>
                    </td>
                    <td>
                      {{ formatCurrencyForDisplay(provider.gross_amount) }}
                    </td>
                    <td>
                      {{ formatCurrencyForDisplay(provider.financed_amount) }}
                    </td>
                    <td>
                      {{ formatCurrencyForDisplay(provider.acquisition_amount) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </ExpandableCard>
      </div>

      <div class="col-12">
        <ExpandableCard
          :key="`financings-${id}`"
          title="Financiamentos"
          :default-expanded="isFinancingsExpanded"
          @update:expanded="isFinancingsExpanded = $event"
        >
          <div
            class="
              d-flex
              flex-column flex-md-row
              justify-content-between
              align-items-start align-items-md-center
              gap-2
              mb-3
            "
          >
            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!canModifyFinancings || isLoading"
                @click="handleOpenAddFinancingsModal"
              >
                Adicionar Financiamentos
              </button>
              <button
                v-if="selectedFinancingIds.size > 0"
                type="button"
                class="btn btn-danger"
                :disabled="!canModifyFinancings"
                @click="handleRemoveFinancings"
              >
                Remover ({{ selectedFinancingIds.size }})
              </button>
            </div>
          </div>

          <div class="row mb-3 align-items-end">
            <div class="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
              <label class="form-label" for="integration_status">Status</label>
              <select
                id="integration_status"
                class="form-select"
                :value="paginationParams.integration_status || ''"
                @change="handleIntegrationStatusChange"
              >
                <option value="">Todos</option>
                <option
                  v-for="option in statusOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
              <label class="form-label" for="search_type">Buscar por:</label>
              <select
                id="search_type"
                class="form-select"
                v-model="localSearchType"
                aria-label="Campo de seleção de tipo de busca"
              >
                <option disabled :value="undefined">Selecione...</option>
                <option
                  v-for="option in FINANCING_SEARCH_TYPE_OPTIONS"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.name }}
                </option>
              </select>
            </div>

            <div class="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
              <label class="form-label" for="search_query" />
              <input
                id="search_query"
                type="text"
                class="form-control"
                v-model="localSearchQuery"
                placeholder="Digite o valor da busca"
                aria-label="Campo de busca"
                :disabled="!localSearchType"
                @keydown.enter="handleSearch"
              />
            </div>

            <div class="col-12 col-sm-6 col-md-3 d-flex align-items-end gap-2">
              <button
                type="button"
                class="btn btn-primary flex-fill flex-sm-initial"
                @click="handleSearch"
              >
                <Search class="icon icon-sm me-1" />
                Buscar
              </button>
              <button
                type="button"
                class="btn btn-secondary flex-fill flex-sm-initial"
                @click="handleClearFilters"
              >
                <FunnelX class="icon icon-sm me-1" />
                Limpar
              </button>
            </div>
          </div>

          <div v-if="financings.length === 0" class="text-muted text-center py-4">
            Nenhum financiamento encontrado
          </div>

          <div v-else>
            <div v-if="financingsMetadata" class="mb-3 text-muted">
              Exibindo {{ financingsMetadata.current_page }} de {{ financingsMetadata.total_pages }} páginas
              • Total de {{ financingsMetadata.total_count }} financiamento(s)
            </div>

            <div class="table-responsive">
              <table class="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th scope="col" style="width: 40px;">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="allFinancingsSelected"
                        :indeterminate="someFinancingsSelected"
                        @change="toggleAllFinancingsSelection"
                        aria-label="Selecionar todos"
                      />
                    </th>
                    <th scope="col">Financiamento ID</th>
                    <th scope="col">Cliente</th>
                    <th scope="col">CCB</th>
                    <th scope="col">Valor Financiado</th>
                    <th scope="col">Valor Bruto</th>
                    <th scope="col">Valor de Aquisição</th>
                    <th scope="col">Data Formalização</th>
                    <th scope="col">Status</th>
                    <th scope="col">Status de Pagamento</th>
                    <th scope="col">Tipo Taxa</th>
                    <th scope="col">Parcelas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="financing in financings"
                    :key="financing.id"
                    style="cursor: pointer;"
                    @click="navigateToFinancingDetail(financing)"
                  >
                    <td @click.stop @mousedown.stop>
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="selectedFinancingIds.has(financing.solfacil_financing_id)"
                        :disabled="isFinancingDisabled(financing)"
                        @change="toggleFinancingSelection(financing.solfacil_financing_id)"
                        @click.stop
                        :aria-label="`Selecionar financiamento ${financing.solfacil_financing_id}`"
                      />
                    </td>
                    <td>
                      <strong>{{ financing.solfacil_financing_id }}</strong>
                    </td>
                    <td>{{ financing.customer_name }}</td>
                    <td>{{ financing.ccb }}</td>
                    <td>
                      <strong>{{ formatCurrencyForDisplay(financing.financed_amount) }}</strong>
                    </td>
                    <td>
                      <strong>{{ formatCurrencyForDisplay(financing.gross_amount) }}</strong>
                    </td>
                    <td>
                      <strong>{{ formatCurrencyForDisplay(financing.acquisition_amount) }}</strong>
                    </td>
                    <td>{{ formatDate(financing.formalization_date) }}</td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(financing.integration_status)">
                        {{ getFinancingStatusLabel(financing.integration_status) }}
                      </span>
                    </td>
                    <td>
                      <span class="badge" :class="getPaymentStatusBadgeClass(financing.payment_status)">
                        {{ getPaymentStatusLabel(financing.payment_status) }}
                      </span>
                    </td>
                    <td>{{ financing.rate_type }}</td>
                    <td>{{ financing.installments_number }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="pagination-wrapper">
              <Pagination
                :current-page="currentPage"
                :has-next="hasNext"
                :page-size="pageSize"
                @page-change="handlePageChange"
                @page-size-change="handlePageSizeChange"
              />
            </div>
          </div>
        </ExpandableCard>
      </div>

      <div class="col-12">
        <ExpandableCard
          :key="`logs-${id}`"
          :title="logsTitle"
          :default-expanded="isLogsExpanded"
          @update:expanded="isLogsExpanded = $event"
        >
          <div v-if="logs.length === 0" class="text-muted text-center py-4">
            Nenhum log encontrado
          </div>

          <div v-else class="table-responsive">
            <table class="table table-vcenter card-table">
              <thead>
                <tr>
                  <th scope="col">Data/Hora</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Descrição</th>
                  <th scope="col">Criado por</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in logs" :key="log.id">
                  <td>{{ formatDateTime(log.created_at) }}</td>
                  <td>
                    <span class="badge" :class="getLogTypeBadgeClass(log.type)">
                      {{ getLogTypeLabel(log.type) }}
                    </span>
                  </td>
                  <td>{{ log.description }}</td>
                  <td>{{ log.created_by || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </ExpandableCard>
      </div>
    </div>

    <ConfirmModal
      v-model="showFinalizeModal"
      :title="finalizeModalTitle"
      :message="finalizeModalMessage"
      type="info"
      :loading="finalizeCessionMutation.isPending.value"
      @confirm="confirmFinalizeCession"
    />

    <ConfirmModal
      v-model="showRemoveModal"
      title="Remover Financiamentos"
      :message="
        `Tem certeza que deseja remover ${selectedFinancingIds.size} financiamento(s) deste ${entityLabelLower}?`
      "
      type="danger"
      :loading="removeFinancingsMutation.isPending.value"
      @confirm="confirmRemoveFinancings"
    />

    <AddFinancingsModal
      v-model="showAddFinancingsModal"
      :cession-id="id"
      :is-loading="addFinancingsMutation.isPending.value"
      @submit="handleAddFinancings"
    />

    <FinancingOperationResultModal
      v-model="showResultModal"
      :operation-type="operationType"
      :results="operationResults"
    />

    <ConfirmModal
      v-model="showProcessModal"
      :title="processModalTitle"
      :message="processModalMessage"
      type="info"
      :loading="processCessionMutation.isPending.value"
      @confirm="confirmProcessCession"
    />
  </DashboardLayout>
</template>

<style scoped lang="scss">
@media (max-width: 576px) {
  .card-body {
    padding: 0.75rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .table {
    th,
    td {
      padding: 0.5rem;
      font-size: 0.875rem;
    }
  }

  .btn {
    font-size: 0.875rem;
    padding: 0.5rem 1rem;
  }
}

@media (max-width: 768px) {
  .table-responsive {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .table {
    min-width: 800px;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
    overflow-x: auto;
    padding: 0 1rem;
  }

  .pagination-wrapper :deep(.d-flex) {
    flex-wrap: wrap;
    justify-content: center !important;
    padding-right: 0 !important;
    width: 100%;
  }
}

@media (min-width: 769px) {
  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
